import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import Sidebar from '../components/Sidebar';
import TopButtons from '../components/common/TopButtons';

import { useAccount, useChainId } from 'wagmi';
import { ethers } from 'ethers';
import { type Config, useConnectorClient } from 'wagmi';
import { clientToSigner } from '../utils/provider';
import Loading from '../components/common/Loading';
import { useToast } from '../contexts/ToastContext';
import { getTokenDistributorAddress } from '../config/contracts';
import TokenDistributorAbi from '../config/TokenDistributor.json';

/** Hook to convert a viem Wallet Client to an ethers.js Signer. */
export function useEthersSigner({ chainId }: { chainId?: number } = {}) {
  const { data: client } = useConnectorClient<Config>({ chainId });
  return useMemo(() => (client ? clientToSigner(client) : undefined), [client]);
}

const HomeTopButtons = styled(TopButtons)`
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #0a0a1a;
  color: white;
  padding: 10px;
  margin-left: 240px;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    margin-left: 0;
    padding-bottom: 80px;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  padding: 0 40px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const SectionTitle = styled.h2`
  color: #4ad0ac;
  font-size: 18px;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  width: 100%;
`;

const Label = styled.label`
  display: block;
  color: #4ad0ac;
  margin-bottom: 5px;
  font-size: 14px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  font-size: 16px;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  
  &:focus {
    outline: none;
    border-color: #4ad0ac;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4d4d;
  font-size: 12px;
  margin-top: 4px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 40px;
`;

const SubmitButton = styled.button`
  width: 100%;
  max-width: 400px;
  padding: 14px;
  background: #4ad0ac;
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover:not(:disabled) {
    background: #3ab090;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TokenDistributor: React.FC = () => {
  const { isConnected } = useAccount();
  const chainId = useChainId();
  const signer = useEthersSigner();
  const { showToast } = useToast();
  
  const [recipientAddress, setRecipientAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [addressError, setAddressError] = useState('');
  const [amountError, setAmountError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // 校验地址格式
  const isValidAddress = (address: string): boolean => {
    try {
      return ethers.isAddress(address);
    } catch {
      return false;
    }
  };

  // 处理地址输入
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRecipientAddress(value);
    setAddressError('');
  };

  // 处理数量输入
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAmount(value);
    setAmountError('');
  };

  // 处理提交
  const handleSubmit = async () => {
    // 重置错误信息
    setAddressError('');
    setAmountError('');

    // 校验地址
    if (!recipientAddress.trim()) {
      setAddressError('请输入接收地址');
      return;
    }

    if (!isValidAddress(recipientAddress)) {
      setAddressError('请输入有效的以太坊地址');
      return;
    }

    // 校验数量
    if (!amount.trim()) {
      setAmountError('请输入转账数量');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setAmountError('请输入有效的数量');
      return;
    }

    // 检查是否连接钱包
    if (!isConnected || !signer) {
      showToast('请先连接钱包', 'error');
      return;
    }

    // 获取合约地址
    const contractAddress = getTokenDistributorAddress(chainId);
    if (!contractAddress) {
      showToast('当前链不支持该合约', 'error');
      return;
    }

    setIsLoading(true);

    try {
      // 创建合约实例
      const contract = new ethers.Contract(
        contractAddress,
        TokenDistributorAbi.abi,
        signer
      );

      // 将数量转换为18位精度
      const amountInWei = ethers.parseEther(amount);

      // 调用transferAToken方法
      const tx = await contract.transferAToken(recipientAddress, amountInWei);
      
      showToast('交易已提交，等待确认...', 'success');
      
      // 等待交易确认
      const receipt = await tx.wait();
      
      if (receipt.status === 1) {
        showToast('转账成功！', 'success');
        // 清空表单
        setRecipientAddress('');
        setAmount('');
      } else {
        showToast('交易失败', 'error');
      }
    } catch (error: any) {
      console.error('Transaction error:', error);
      
      let errorMessage = '转账失败，请检查钱包是否链接正确';
      // let errorMessage = '转账失败';
      // if (error.code === 'ACTION_REJECTED' || error.code === 4001) {
      //   errorMessage = '交易已被用户取消';
      // } else if (error.code === 'INSUFFICIENT_FUNDS' || error.message?.includes('insufficient')) {
      //   errorMessage = '余额不足';
      // } else if (error.message) {
      //   errorMessage = error.message;
      // }
      
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Sidebar />
      <Container>
        <HomeTopButtons />
        <FormContainer>
          <SectionTitle>代币分发</SectionTitle>
          
          <FormGroup>
            <Label>发财地址</Label>
            <InputWrapper>
              <Input
                type="text"
                placeholder="请输入接收地址"
                value={recipientAddress}
                onChange={handleAddressChange}
                disabled={isLoading}
              />
            </InputWrapper>
            {addressError && <ErrorMessage>{addressError}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>发财数量</Label>
            <InputWrapper>
              <Input
                type="text"
                placeholder="请输入转账数量"
                value={amount}
                onChange={handleAmountChange}
                disabled={isLoading}
              />
            </InputWrapper>
            {amountError && <ErrorMessage>{amountError}</ErrorMessage>}
          </FormGroup>

          <ButtonContainer>
            <SubmitButton
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? '飞行中...' : '起飞'}
            </SubmitButton>
          </ButtonContainer>
        </FormContainer>
        {isLoading && <Loading />}
      </Container>
    </>
  );
};

export default TokenDistributor;